type StoreLocatorFilterOption = {
  key: string;
  is_available: "true" | "false";
};

export type ShopSchedule = {
  name: string;
  closed: boolean;
  open_time: string;
  close_time: string;
  break_end_time: string;
  break_start_time: string;
};

export type StoreLocatorOpeningHours = {
  days: ShopSchedule[];
  time_zone: string;
  general_announcement: string;
};

export type FlotiqApiItem<TItem> = {
  item: TItem;
};

export type FlotiqSearchApiResponse<TItem> = {
  data: TItem[];
  total_count: number;
};

export type FlotiqGeoSearchParams = {
  radius: number;
  lat: number;
  lng: number;
  limit?: number;
  orderBy?: string;
  contentType: string;
};

export type StoreLocatorProductAttribute = {
  key: string;
  category: string;
  values: { value: string | number }[];
};

export type FlotiqCoordinates = {
  lat: number;
  lon: number;
};

export type FlotiqMapEntityBase = {
  id: string;
  Status: boolean;
  Location: FlotiqCoordinates;
  City?: string;
  State?: string;
  Street?: string;
  Country?: string;
  ZipCode?: string;
};

export type FlotiqLinkFields = {
  CtaHref?: string;
  CtaLabel?: string;
  UrlHref?: string;
  urlLabel?: string;
};

/**
 * used in storeLocator
 */

export type LegacyStoreType = "ConceptStore" | "PloomCorner";

export const BRAND_STORES = ["Flagship Store", "Brand Store", "Pop-up Store"] as const;
export type BrandStore = (typeof BRAND_STORES)[number];

export const OTHER_STORES = [
  "Tobacconist",
  "Tobacconist with Vending Machine",
  "Duty Free",
  "Vending Machine",
  "Bars/Cafes/Restaurants",
  "Bar Tobacconist",
  "Bar Tobacconist with Vending Machine",
] as const;
export type OtherStore = (typeof OTHER_STORES)[number];

export type StoreType = BrandStore | OtherStore | LegacyStoreType;

export type FlotiqStore = FlotiqMapEntityBase &
  FlotiqLinkFields & {
    ExternalId: string;
    StoreName: string;
    City_slug?: string;
    address: string;
    opening_hours?: StoreLocatorOpeningHours[];
    Hours?: string;
    State_slug?: string;
    StoreImageUrl?: string;
    PhoneNumber?: string;
    product_types?: StoreLocatorFilterOption[];
    product_types_vending_machine?: StoreLocatorFilterOption[];
    services?: StoreLocatorFilterOption[];
    product_attributes?: StoreLocatorProductAttribute[];
    product_attributes_vending_machine?: StoreLocatorProductAttribute[];
    HoursParagraph?: string;
    Description?: string;
    storetype?: StoreType;
  };

type FlotiqPagination = { total_count: number; total_pages: number; current_page: number; count: number };

export type FlotiqInStoreDeliveryStore = FlotiqStore & {
  Street: string;
  City: string;
  ZipCode: string;
  StoreName: string;
  address: string;
  Country: string;
};

export type FlotiqStoreApiItem = FlotiqApiItem<Omit<FlotiqStore, "address"> & Record<string, unknown>>;

export type FlotiqStoreApiResponse = FlotiqSearchApiResponse<FlotiqStoreApiItem> & {
  summary: {
    aggregations: Record<
      string,
      {
        buckets: {
          key: string;
          doc_count: number;
        }[];
      }
    >;
  };
} & FlotiqPagination;

export type FlotiqAreaApiResponse = {
  data: {
    id: string;
    name: string;
    slug: string;
    map_zoom: number;
    center_coordinates: { lat: number; lon: number };
  }[];
} & FlotiqPagination;

export type FlotiqStoreDetailApiResponse = Omit<FlotiqStore, "address">;

export type FlotiqStorePrizeLocator = {
  Description?: string;
  Categories?: { key: string }[];
} & FlotiqStore;

export type FlotiqEvent = FlotiqMapEntityBase &
  FlotiqLinkFields & {
    StartDate: string;
    EndDate: string;
    EventId?: string;
    EventName?: string;
  };

export type FlotiqEventApiItem = FlotiqApiItem<FlotiqEvent>;

export type FlotiqEventApiResponse = FlotiqSearchApiResponse<FlotiqEventApiItem>;

export type FlotiqHoreca = FlotiqMapEntityBase &
  FlotiqLinkFields & {
    HorecaId: string;
    HorecaName: string;
    opening_hours?: StoreLocatorOpeningHours[];
    City: string;
    Street: string;
    ZipCode: string;
  };

export type FlotiqHorecaApiItem = FlotiqApiItem<FlotiqHoreca>;

export type FlotiqHorecaApiResponse = FlotiqSearchApiResponse<FlotiqHorecaApiItem>;
